import { apiClient } from '../axiosInstance';
import { ENDPOINTS } from '../endpoints';
import type {
  AvailabilityService,
  AvailableDatesParams,
  TimeSlotsParams,
} from './interfaces/AvailabilityService';

export const availabilityService: AvailabilityService = {
  async fetchAvailableDates(params: AvailableDatesParams) {
    const today = new Date();
    const firstOfMonth = new Date(params.year, params.month - 1, 1);
    const d = firstOfMonth > today ? firstOfMonth : today;
    const pad = (n: number) => String(n).padStart(2, '0');
    const serviceDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const { data } = await apiClient.get<Record<string, unknown>>(
      ENDPOINTS.v1.availability.availableDates,
      {
        params: {
          service_date: serviceDate,
          expected_duration: params.expected_duration,
          service_category_id: params.service_category_id,
          postal_code: params.postal_code,
        },
      },
    );
    return data;
  },

  async fetchTimeSlots(params: TimeSlotsParams) {
    const { data } = await apiClient.get<Record<string, unknown>[]>(
      ENDPOINTS.v1.availability.availableTimeSlots,
      {
        params: {
          service_date: params.date,
          expected_duration: params.expected_duration,
          service_category_id: params.service_category_id,
          postal_code: params.postal_code,
        },
      },
    );
    return data.map((s) => {
      const startsAt = (s['start_time'] ?? s['starts_at'] ?? '') as string;
      const endsAt = (s['end_time'] ?? s['ends_at'] ?? '') as string;
      const syntheticId =
        (s['id'] as number | undefined) ??
        (startsAt ? new Date(startsAt).getTime() : Math.random());
      // quick-260523-swa Task 8 — preserve the three iso fields emitted by
      // the backend availability controller. Defensive fallbacks point at
      // starts_at / ends_at so a transitional or older response without the
      // new fields still produces a complete TimeSlot.
      //
      // MD-03 fix (code review): use a truthy guard (||) instead of `??` so
      // that a backend-emitted empty string `""` falls back to starts_at /
      // ends_at rather than being preserved and later submitted as
      // appointment.arrival_window_start = "".
      const scheduledTime = (s['scheduled_time'] as string) || startsAt;
      const arrivalStart = (s['arrival_window_start'] as string) || startsAt;
      const arrivalEnd = (s['arrival_window_end'] as string) || endsAt;
      // quick-260528-od4 (PODZ-1702) — preserve the backend `disabled` flag
      // so TimeSlots.vue can disable individual buttons the backend already
      // rejected. `Boolean(...)` (not `??`) maps undefined / null / missing
      // to false so the wizard never receives undefined.
      const isDisabled = Boolean(s['disabled']);
      return {
        id: syntheticId,
        starts_at: startsAt,
        ends_at: endsAt,
        scheduled_time: scheduledTime,
        arrival_window_start: arrivalStart,
        arrival_window_end: arrivalEnd,
        disabled: isDisabled,
      };
    });
  },
};
